import clsx from 'clsx'
import React, {ComponentProps, useState} from 'react'
import {isNonBlankString} from '../../responseRefiners'
import MaskButton from '../MaskButton'

type Props = Readonly<{
  disabled?: boolean
  errorMessage?: string | null
  id: string
  label: string
}> &
  Omit<ComponentProps<'input'>, 'className'>

/**
 * A masked Password input with toggle mask button and validation label.
 *
 * @see {@link Props}.
 */
function PasswordInput({
  disabled = false,
  errorMessage = null,
  id,
  label,
  onChange,
  ...inputProps
}: Props) {
  const errorId = `${id}-error`
  const isError = isNonBlankString(errorMessage)
  const labelId = `${id}-label`
  const [masked, setMasked] = useState(true)
  const [password, setPassword] = useState('')

  return (
    <div className="relative">
      <label
        className="typography-subtitle-1 inline-block pb-[11.5px] pl-4 text-navy"
        htmlFor={id}
        id={labelId}
      >
        {label}
      </label>
      <div className="relative">
        <input
          {...inputProps}
          aria-errormessage={errorId}
          aria-invalid={isError}
          aria-labelledby={labelId}
          className={clsx(
            'typography-subtitle-1 min-h-9 w-full appearance-none rounded-full bg-[#ffc212] px-4 text-white outline-none',
            'placeholder-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy',
            isError && `ring-2 ring-red`
          )}
          disabled={disabled}
          id={id}
          name={id}
          onChange={event => {
            setPassword(event.target.value)
            onChange?.(event)
          }}
          type={masked ? 'password' : 'text'}
          value={password}
        />
        <div className="absolute right-0 top-1.5 mr-4 h-6 w-6">
          <MaskButton masked={masked} onClick={() => setMasked(!masked)} />
        </div>
      </div>
      {isError && (
        <label
          className="typography-body-2 ml-4 mt-1 w-full text-left text-red"
          id={errorId}
        >
          {errorMessage}
        </label>
      )}
    </div>
  )
}

export default PasswordInput
